import { createFileRoute, Link } from '@tanstack/react-router';
import { Section, SectionEyebrow } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/how-bookings-are-allocated')({
  component: HowBookingsAreAllocatedPage,
});

const guardrails: { title: string; body: React.ReactNode }[] = [
  {
    title: 'Every required staff member is free for the full duration',
    body: (
      <>
        A slot is only offered when the staff the service requires are free
        for the entire service window — plus any before/after buffer, plus
        travel time for on-site jobs. We don&rsquo;t round, we don&rsquo;t
        squeeze.
      </>
    ),
  },
  {
    title: 'Every required resource is free',
    body: (
      <>
        Rooms, tables, hoists, dental chairs, bays — whatever the service
        declares it needs, the allocator checks that an instance of each is
        free for the full window. Two services that need the same CT room
        will never both be offered the same 10am.
      </>
    ),
  },
  {
    title: 'At least one staff member holds the required skills',
    body: (
      <>
        Services declare credentials (CT certified, registered nurse,
        Cert III in pet grooming, mortgage broker accreditation). Only
        qualified staff are considered. Expired credentials are treated as
        missing.
      </>
    ),
  },
  {
    title: 'Travel time fits between neighbouring on-site jobs',
    body: (
      <>
        For mobile services, the allocator pads each job with the configured
        travel buffer on both sides, so back-to-back jobs in opposite
        suburbs are never offered. The check is per-booking and real-time —
        no batch overnight re-planning.
      </>
    ),
  },
  {
    title: 'Postcode whitelist (if you use one)',
    body: (
      <>
        For mobile businesses with a service-area whitelist, the
        client&rsquo;s address must sit inside the list before any slot is
        offered. Out-of-area requests are refused at the booking page, not
        accepted and apologised for later.
      </>
    ),
  },
];

interface ServiceMode {
  key: string;
  label: string;
  description: string;
  example: string;
  notConsidered: string;
}

const serviceModes: ServiceMode[] = [
  {
    key: 'on_premises',
    label: 'At our place (on-premises)',
    description:
      'The service happens at your address. The allocator matches required staff plus a required room or piece of equipment. Buffers before and after the appointment are honoured. Travel time is not considered — nobody is going anywhere.',
    example:
      'A physio clinic with two consult rooms and one treatment room. A 45-minute physio booking only lights up when the treatment room is free and a physio is on the roster for that window.',
    notConsidered:
      'Doesn’t consider drive-time for the client. Doesn’t consider parking. Doesn’t bunch on-premises bookings by therapist preference.',
  },
  {
    key: 'on_site',
    label: 'At your place (on-site / mobile)',
    description:
      'Your staff travel to the client. The allocator pads the booking with a travel buffer on both sides, and refuses any slot that would put the same staff member in two suburbs without enough drive-time in between. The postcode whitelist (if configured) gates the booking at the form.',
    example:
      'A lawn-care business covering Sydney’s Inner West. A Tuesday-morning hedge trim in Marrickville lights up only if the assigned gardener has 25 minutes of buffer after their 8am Stanmore job.',
    notConsidered:
      'Doesn’t re-plan the day to minimise total driving (no route optimisation / TSP). Doesn’t use live traffic — buffers are a fixed per-service value. Optionally considers staff home base via the closest-to-booking strategy (see below).',
  },
  {
    key: 'virtual',
    label: 'Virtual (video)',
    description:
      'Held over video. The allocator skips the resource check entirely (there is no room) but still matches required staff, skills and duration. The booking page tags the service "Virtual" so clients don’t turn up at your door.',
    example:
      'A mortgage broker offers a 30-minute video consult. The allocator only needs the broker to be free for 30 minutes inside their working hours — no room is consumed.',
    notConsidered:
      'Doesn’t generate the meeting link (we send the time; you bring the room). Doesn’t check the staff member’s camera, network or background.',
  },
  {
    key: 'phone',
    label: 'Phone',
    description:
      'A scheduled phone call. Identical to virtual from the allocator’s point of view — staff and skills only, no room. Useful for quick triage, advice, or follow-up calls that don’t need video.',
    example:
      'An accountant offers a 15-minute "quick question" phone slot. The allocator books it against the accountant’s diary and sends the client a confirmation with the time. The accountant calls them.',
    notConsidered:
      'Doesn’t place the call, doesn’t record it, doesn’t check the line.',
  },
];

interface IndustryShape {
  key: string;
  title: string;
  body: React.ReactNode;
  caveat: React.ReactNode;
}

const industryShapes: IndustryShape[] = [
  {
    key: 'allied-health',
    title: 'Allied health (clinic with rooms)',
    body: (
      <>
        Most clinic services declare both a credential (e.g.{' '}
        <em>physiotherapist</em>) and a required room feature (e.g.{' '}
        <em>treatment room</em>). The allocator picks the cheapest match —
        the most over-specified room is only chosen if nothing simpler is
        free. Staff are picked independently from the qualified pool.
      </>
    ),
    caveat: (
      <>
        <strong>You can get a soft &ldquo;one practitioner, one
        room&rdquo; pattern</strong> by setting the service to{' '}
        <em>same resource as staff today</em> &mdash; once Dr Smith has
        used Room 2 once, the allocator keeps her in Room 2 for the rest
        of her day. What we don&rsquo;t do yet is <em>reserve</em>{' '}
        Room 2 for Dr Smith before her first booking lands. If you need
        hard ownership, you can still encode it as a per-practitioner
        room resource and require both on the service.
      </>
    ),
  },
  {
    key: 'mobile-trades',
    title: 'Mobile trades, gardening, pool care',
    body: (
      <>
        Each service carries a travel buffer (typically 15&ndash;45 minutes)
        and the business carries an optional postcode whitelist. The
        allocator combines both: a slot is only offered when the assigned
        staff member has enough drive-time padding either side, and the
        client&rsquo;s address is inside the whitelist.
      </>
    ),
    caveat: (
      <>
        <strong>Bookaburra picks per booking, not per day.</strong> There
        is no route optimisation. If a hot lead lands in suburb A and the
        day&rsquo;s morning is already in suburb B, the allocator may offer
        a slot that crosses the city &mdash; it&rsquo;s valid by the
        travel-buffer rule, just not optimal. The pragmatic workaround
        today: closed-day overrides plus service-specific business hours to
        steer the day in advance.
      </>
    ),
  },
  {
    key: 'pets-mechanics',
    title: 'Mobile pet grooming and mechanics',
    body: (
      <>
        Mobile grooming behaves like mobile trades &mdash; travel buffer
        plus postcode whitelist. Mechanics adds the bonus that workshop
        bays are modelled as resources, so single-occupancy is enforced by
        the resource matcher: two cars never share a hoist.
      </>
    ),
    caveat: (
      <>
        Same limitations as mobile trades. Mechanics with a fixed
        &ldquo;Bay 3 is the alignment bay&rdquo; pattern can encode that
        as a required resource feature; bookings then only flow into the
        right bay.
      </>
    ),
  },
  {
    key: 'professional-services',
    title: 'Professional services (accountants, mortgage brokers, planners)',
    body: (
      <>
        Most appointments are room-plus-duration, sometimes virtual or
        phone. The allocator does the obvious thing: hold the right
        meeting room and the right person for the configured duration.
        Recall frequency (six months, twelve months) drives the
        admin&rsquo;s follow-up list rather than the allocator itself.
      </>
    ),
    caveat: (
      <>
        Partners with different cost shapes (a salaried associate vs a
        contractor on a per-meeting fee) are handled by the staff cost
        tiers below &mdash; the salaried partner&rsquo;s diary fills
        first, the contractor only gets pulled in once the cheaper
        seats are taken.
      </>
    ),
  },
];

interface StaffTier {
  key: string;
  label: string;
  description: string;
}

const staffTiers: StaffTier[] = [
  {
    key: 'full_time',
    label: 'Full-time',
    description:
      'On payroll regardless of bookings. Filling their diary costs you nothing extra, so the allocator tries them first.',
  },
  {
    key: 'part_time',
    label: 'Part-time',
    description:
      'Guaranteed hours on agreed days. Like full-time, those hours are already paid — fill them before anything that costs marginal dollars. A person who works every Tuesday 9–5 is part-time, not paid-hourly.',
  },
  {
    key: 'paid_per_service',
    label: 'Paid per service',
    description:
      'A flat fee each time they perform a service (commission, contractor day-rate, sessional). The marginal cost is the service fee — cheaper than billing a paid-hourly contractor for a full hour to do a 20-minute job.',
  },
  {
    key: 'paid_hourly',
    label: 'Paid hourly',
    description:
      'Billed by the hour, no guaranteed shift. The allocator places these last because hourly billing snaps to whole hours: bring someone in for a 20-minute job and you still pay the full hour. Reserve them for overflow, not the default. A staff member with a guaranteed Tuesday 9–5 belongs in part-time.',
  },
];

interface ServiceStrategy {
  key: string;
  label: string;
  description: string;
  whenToUse: string;
}

const staffStrategies: ServiceStrategy[] = [
  {
    key: 'fill_first_today',
    label: 'Fill first today (default for new services)',
    description:
      'Within each cost tier, the allocator prefers staff who already have bookings today. The goal is to keep one diary busy before opening another &mdash; useful when a half-day for two staff costs the same as a full day for one.',
    whenToUse:
      'Default for most service businesses. Especially good for paid-per-service or paid-hourly staff where empty gaps are wasted money.',
  },
  {
    key: 'round_robin_today',
    label: 'Round-robin today',
    description:
      'Within each cost tier, the allocator prefers staff with the fewest bookings today. Levels out load across the team so nobody&rsquo;s diary is back-to-back while another&rsquo;s sits half empty.',
    whenToUse:
      'Set this on services where staff strongly prefer even rotation &mdash; salons, allied health rooms where each practitioner builds their own book.',
  },
  {
    key: 'first_available',
    label: 'First available',
    description:
      'Whichever qualified staff member is free at the earliest time wins. No load-shaping, no fairness &mdash; just the next clear slot.',
    whenToUse:
      'The simplest behaviour. Set this if you don&rsquo;t care who takes the work, only that it gets booked quickly.',
  },
  {
    key: 'prefer_closest_to_booking',
    label: 'Closest to booking (on-site)',
    description:
      'For on-site services, ranks candidates by straight-line distance from their home postcode to the booking address. The nearest staff wins. Staff without a home postcode set sort to the end &mdash; still bookable, just least preferred. Behaves like first-available for non-on-site services.',
    whenToUse:
      'Mobile trades, mowing, mobile vet, mobile dog grooming &mdash; anywhere staff live across a wide footprint and travel time dominates the cost of a job.',
  },
];

const resourceStrategies: ServiceStrategy[] = [
  {
    key: 'prefer_same_as_staff_today',
    label: 'Same resource as staff today (default for new services)',
    description:
      'If the picked staff member has already used a specific room/chair/bay today, the allocator tries that same resource first. Over a full day this emerges as a soft &ldquo;one staff member, one room&rdquo; pattern &mdash; without you having to hard-wire ownership.',
    whenToUse:
      'Allied health clinics, salons, anywhere practitioners trail equipment or personal touches into their room and would rather not bounce between three of them.',
  },
  {
    key: 'first_available',
    label: 'First available resource',
    description:
      'The cheapest matching resource that&rsquo;s free wins, regardless of who&rsquo;s used what earlier in the day. Pure availability.',
    whenToUse:
      'Workshops, treatment beds, generic rooms &mdash; anywhere the equipment is interchangeable and shuffling staff between rooms is fine.',
  },
];

const notYet: { title: string; body: React.ReactNode }[] = [
  {
    title: 'No per-staff territory carving (only per-staff postcode blacklists)',
    body: (
      <>
        There&rsquo;s one postcode whitelist per business, not per staff
        member &mdash; so you can&rsquo;t today say &ldquo;Lara handles
        2026&ndash;2034, Sam handles 2035&ndash;2049&rdquo; positively. You
        can, however, blacklist individual postcodes per staff member
        (the &ldquo;Won&rsquo;t service&rdquo; list on the Members page)
        which the allocator treats as a hard exclusion. Combined with the
        closest-to-booking strategy, that&rsquo;s usually enough to keep
        each staff member inside their natural footprint without needing
        explicit territory zones.
      </>
    ),
  },
  {
    title: 'No day-end route optimisation (TSP)',
    body: (
      <>
        Each new booking is fitted into the current day; we don&rsquo;t
        rewrite the day later to minimise total driving. Travel buffers
        prevent impossible runs but don&rsquo;t produce a tidy single
        loop.
      </>
    ),
  },
  {
    title: 'No hard-wired room-for-the-day ownership',
    body: (
      <>
        The &ldquo;same resource as staff today&rdquo; strategy gives you a
        soft version of this for free &mdash; once Dr Smith uses Room 2,
        the allocator keeps trying Room 2 for the rest of her day. But
        there&rsquo;s no setting today that <em>reserves</em> Room 2 for
        Dr Smith before her first booking lands, or blocks others from
        using it if she&rsquo;s not in.
      </>
    ),
  },
  {
    title: 'No roster / shift assignment',
    body: (
      <>
        Bookaburra picks who serves each booking. It doesn&rsquo;t build
        next week&rsquo;s shift roster, allocate floor coverage, or
        balance hours across a fortnight. Use a separate rostering tool
        and feed the result in as staff working-hours.
      </>
    ),
  },
  {
    title: 'No cross-day balancing',
    body: (
      <>
        Round-robin and fill-first work within <em>today</em>. The
        allocator doesn&rsquo;t look at the rest of the week to even out
        who&rsquo;s had the busiest run, or to push a quiet morning to
        someone who had a heavy Tuesday.
      </>
    ),
  },
];

function HowBookingsAreAllocatedPage() {
  usePageTitle('How bookings are allocated');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            How it works
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4rem]">
            Honest about how slots get picked.
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            This page exists so you can decide before signup whether
            Bookaburra&rsquo;s allocator fits the way your business actually
            runs. We&rsquo;ll walk through what the allocator always does,
            what each service mode considers, how each industry shape works
            in practice, the staff cost tiers and per-service strategies
            that decide who gets picked &mdash; and the operating patterns
            we don&rsquo;t yet support.
          </p>
          <div className="mt-8 max-w-2xl rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-6 text-base text-brand-ink-soft/80 leading-relaxed">
            <p>
              The allocator is a pure function: same inputs, same answer.
              No randomness, no &ldquo;AI judgement&rdquo; in the booking
              path. If it offers a slot, the slot is valid by the rules on
              this page &mdash; nothing more, nothing less.
            </p>
          </div>
        </div>
      </section>

      {/* GUARDRAILS */}
      <Section tone="shadow">
        <div className="max-w-3xl">
          <SectionEyebrow>The five guardrails</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            What the allocator always does.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            Every slot offered to a client clears all five of these checks.
            If one fails, the slot doesn&rsquo;t appear.
          </p>
        </div>
        <ul className="mt-12 grid gap-5 sm:grid-cols-2">
          {guardrails.map((g) => (
            <li
              key={g.title}
              className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-brand"
            >
              <h3 className="text-lg font-semibold text-brand-ink-soft">
                {g.title}
              </h3>
              <div className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                {g.body}
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {/* SERVICE MODES */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>Four service modes</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            What the allocator considers, by service type.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            Every service is tagged with one of four modes. The mode
            determines which checks the allocator runs.
          </p>
        </div>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {serviceModes.map((m) => (
            <div
              key={m.key}
              className="rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-7"
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-sage/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage">
                {m.label}
              </div>
              <p className="mt-4 text-base leading-relaxed text-brand-ink-soft/85">
                {m.description}
              </p>
              <div className="mt-4 rounded-xl border border-brand-sage/10 bg-brand-cream p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-ink-soft/60">
                  Example
                </p>
                <p className="mt-2 text-sm leading-relaxed text-brand-ink-soft/80">
                  {m.example}
                </p>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-brand-ink-soft/70">
                <span className="font-semibold text-brand-ink-soft/80">
                  Not considered:
                </span>{' '}
                {m.notConsidered}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* INDUSTRY SHAPES */}
      <Section tone="shadow">
        <div className="max-w-3xl">
          <SectionEyebrow>Industry shapes</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            How each business shape works in practice.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            The same allocator handles every vertical &mdash; the
            difference is which fields you set on each service. Here&rsquo;s
            how that lands, and where the model has rough edges.
          </p>
        </div>
        <div className="mt-12 grid gap-6">
          {industryShapes.map((s) => (
            <div
              key={s.key}
              className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-brand"
            >
              <h3 className="text-xl font-bold tracking-tight text-brand-ink-soft">
                {s.title}
              </h3>
              <div className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                {s.body}
              </div>
              <div className="mt-4 rounded-xl border border-brand-gold/30 bg-brand-gold/10 p-4 text-sm leading-relaxed text-brand-ink-soft/80">
                {s.caveat}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* STAFF COST TIERS */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>Staff cost tiers</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            Cheaper staff get the work first.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            Every team member is tagged with one of four cost shapes. The
            allocator walks the list in priority order &mdash; full-time
            first, hourly last &mdash; and only opens the next tier once
            nobody in the current one can take the booking. You can
            reorder the priority per service if a particular job inverts
            the usual logic, but the default is the same waterfall for
            every business.
          </p>
        </div>
        <ul className="mt-12 grid gap-5 sm:grid-cols-2">
          {staffTiers.map((tier) => (
            <li
              key={tier.key}
              className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-brand"
            >
              <h3 className="text-lg font-semibold text-brand-ink-soft">
                {tier.label}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                {tier.description}
              </p>
            </li>
          ))}
        </ul>
        <div className="mt-10 max-w-3xl rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-6 text-base text-brand-ink-soft/80 leading-relaxed">
          <p>
            <strong>Why paid-hourly sits at the bottom by default.</strong>{' '}
            Hourly billing snaps to whole hours &mdash; if you bring an
            hourly contractor in for a 20-minute job, you pay for the
            full hour either side. Filling a full-time or part-time
            diary first (already paid) and only spilling into hourly
            when nothing else fits is almost always the cheaper choice.
          </p>
        </div>
      </Section>

      {/* PER-SERVICE STRATEGIES */}
      <Section tone="shadow">
        <div className="max-w-3xl">
          <SectionEyebrow>Per-service strategies</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            How the allocator chooses within a tier.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            Cost tiers narrow the field. Two further dials decide how to
            pick between staff inside the cheapest tier that can take the
            job, and which room or resource to pair them with. Both are
            set per service so a clinic can rotate massage therapists
            fairly while still piling consults onto one GP.
          </p>
        </div>
        <div className="mt-10">
          <h3 className="text-xl font-semibold text-brand-ink-soft">
            Staff strategy
          </h3>
          <ul className="mt-5 grid gap-5 lg:grid-cols-3">
            {staffStrategies.map((s) => (
              <li
                key={s.key}
                className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-6 shadow-brand"
              >
                <h4 className="text-base font-semibold text-brand-ink-soft">
                  {s.label}
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-brand-ink-soft/80">
                  {s.description}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-brand-ink-soft/70">
                  <span className="font-semibold text-brand-ink-soft/80">
                    Good when:
                  </span>{' '}
                  {s.whenToUse}
                </p>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-12">
          <h3 className="text-xl font-semibold text-brand-ink-soft">
            Resource strategy
          </h3>
          <ul className="mt-5 grid gap-5 lg:grid-cols-2">
            {resourceStrategies.map((s) => (
              <li
                key={s.key}
                className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-6 shadow-brand"
              >
                <h4 className="text-base font-semibold text-brand-ink-soft">
                  {s.label}
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-brand-ink-soft/80">
                  {s.description}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-brand-ink-soft/70">
                  <span className="font-semibold text-brand-ink-soft/80">
                    Good when:
                  </span>{' '}
                  {s.whenToUse}
                </p>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-10 max-w-3xl rounded-2xl border border-brand-sage/15 bg-brand-cream p-6 text-base text-brand-ink-soft/80 leading-relaxed">
          <p>
            Services migrated from before this change keep the old
            behaviour &mdash; <em>first available</em> staff and resource
            &mdash; so existing bookings flow the same way they always
            have. New services default to <em>fill first today</em> plus{' '}
            <em>same resource as staff today</em>, which is the right
            call for the typical clinic, mobile crew or salon.
          </p>
        </div>
      </Section>

      {/* PROXIMITY — HONEST CAVEAT */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>Closest-to-booking, honestly</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            Straight-line distance &mdash; with a manual override.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            The closest-to-booking strategy ranks staff by the great-circle
            (&ldquo;as the crow flies&rdquo;) distance from their home
            postcode to the booking address. It&rsquo;s free to compute, it
            updates instantly, and it costs you nothing to leave on.
          </p>
          <p className="mt-4 text-lg text-brand-ink-soft/75">
            What it can&rsquo;t see is roads. Werribee to Geelong is 30 km
            on the map but 50 minutes by freeway around the bay. Sorrento
            to Queenscliff is 5 km across Port Phillip Bay but a 2-hour
            drive around it. The crow flies; your van takes the West Gate.
          </p>
          <p className="mt-4 text-lg text-brand-ink-soft/75">
            We chose haversine over a live driving-time service on
            purpose. A directions call costs roughly half a US cent each;
            the allocator considers many candidates across many slot
            offers per booking visit, so at modest scale the bill would
            run into tens of dollars per business per day &mdash; for a
            problem that&rsquo;s usually solved by one line in a list.
          </p>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-brand">
            <h3 className="text-lg font-semibold text-brand-ink-soft">
              The safety valve: per-staff postcode blacklists
            </h3>
            <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
              On each staff member you can list postcodes they shouldn&rsquo;t
              be sent to, with an optional free-text reason
              (&ldquo;across the bay&rdquo;, &ldquo;no Harbour Bridge
              route&rdquo;). The allocator treats the list as a hard
              exclusion &mdash; that staff member is removed from the
              candidate pool for on-site bookings in those postcodes,
              regardless of which staff strategy the service uses.
            </p>
            <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
              The reason field surfaces as a tooltip on the Members page
              so the team six months from now still understands why an
              entry exists.
            </p>
          </div>
          <div className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-brand">
            <h3 className="text-lg font-semibold text-brand-ink-soft">
              Staff without a home postcode
            </h3>
            <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
              Leaving the home postcode blank is fine. Those staff sort
              to the end of the proximity ranking &mdash; still bookable
              when nobody nearer is free, just least preferred. Use this
              for casuals who genuinely cover anywhere you send them.
            </p>
            <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
              The strategy is also a no-op for non-on-site services. A
              clinic running &ldquo;closest to booking&rdquo; on a virtual
              telehealth call falls back to first-available
              automatically &mdash; no failed bookings.
            </p>
          </div>
        </div>
      </Section>

      {/* ALLOCATION LOG */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>The allocation log</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            We record who got picked &mdash; and who didn&rsquo;t.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            For every booking, we save the staff member and resource
            that won the slot, plus an ordered list of who would have
            been picked next if the first choice had said no. The team
            can open any booking and see the full reasoning: which tier
            it landed in, what strategy was applied, and which other
            staff were in the running.
          </p>
          <p className="mt-4 text-lg text-brand-ink-soft/75">
            There&rsquo;s no black box. If you ever look at a roster and
            think &ldquo;why did Dave get this one?&rdquo;, the answer
            is one click away.
          </p>
        </div>
      </Section>

      {/* WHAT WE DON'T DO YET */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>What Bookaburra doesn&rsquo;t do yet</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            The operating models we don&rsquo;t yet fit.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            If your business runs on one of these patterns, we&rsquo;d
            rather you know now than find out three weeks in.
          </p>
        </div>
        <ul className="mt-12 grid gap-5 sm:grid-cols-2">
          {notYet.map((n) => (
            <li
              key={n.title}
              className="rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-7"
            >
              <h3 className="text-lg font-semibold text-brand-ink-soft">
                {n.title}
              </h3>
              <div className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                {n.body}
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-10 max-w-3xl rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-6 text-base text-brand-ink-soft/80 leading-relaxed">
          <p>
            These are deliberate omissions &mdash; solving each one well
            takes a year of careful work, and most service businesses
            don&rsquo;t need them. If you do,{' '}
            <Link
              to="/contact"
              className="font-medium text-brand-sage underline-offset-4 hover:underline"
            >
              get in touch
            </Link>
            . We&rsquo;re prioritising what real customers ask for.
          </p>
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-24">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
            Still the right fit?
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-brand-cream/80">
            If the guardrails above describe how you&rsquo;d like
            bookings to behave, you&rsquo;ll feel at home. If something on
            the limits list is a deal-breaker,{' '}
            <Link
              to="/contact"
              className="font-medium underline-offset-4 hover:underline"
            >
              tell us
            </Link>
            {' '}&mdash; that&rsquo;s how the roadmap gets shaped.
          </p>
        </div>
      </section>
    </>
  );
}
